import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/hooks/use-auth';
import { DashboardLayout } from '@/layouts/dashboard-layout';
import { MODULES, isModuleEnabled } from '@/lib/modules';
import { ToastHost } from '@/components/ui/toast';
import LoginPage from '@/pages/login';
import RegisterPage from '@/pages/register';
import OverviewPage from '@/pages/overview';
import PulsePage from '@/pages/pulse';
import GrowthPage from '@/pages/growth';
import MonitorPage from '@/pages/monitor';
import CreditLogPage from '@/pages/credit-log';
import XhsDiagnosisPage from '@/pages/xhs-diagnosis';
import LibraryPage from '@/pages/library';
import PersonaPage from '@/pages/persona';
import ContentPage from '@/pages/content';
import ProjectPage from '@/pages/project';
import AssetsPage from '@/pages/assets';
import AdminPage from '@/pages/admin';
import TemplatesPage from '@/pages/templates';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="skeleton-shimmer h-8 w-32 rounded" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  // 模块可见性守卫（全部模块由注册表生成）：非管理员访问被关闭的模块 → 重定向到首个可见模块
  if (user.role !== 'admin') {
    const modules = (user as any)?.permissions?.modules || {};
    const modulePaths = MODULES.map((m) => ({ path: m.path, enabled: isModuleEnabled(modules, m.key) }));
    // 后台管理不纳入可见性设置，始终仅管理员：非管理员 URL 直入一律重定向
    if (location.pathname === '/admin') {
      return <Navigate to={modulePaths.find((m) => m.enabled)?.path || '/login'} replace />;
    }
    const current = modulePaths.find((m) => m.path === location.pathname);
    if (current && !current.enabled) {
      const fallback = modulePaths.find((m) => m.enabled)?.path || '/login';
      return <Navigate to={fallback} replace />;
    }
  }
  return <DashboardLayout>{children}</DashboardLayout>;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ToastHost />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <OverviewPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pulse"
            element={
              <ProtectedRoute>
                <PulsePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/growth"
            element={
              <ProtectedRoute>
                <GrowthPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/monitor"
            element={
              <ProtectedRoute>
                <MonitorPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/credits"
            element={
              <ProtectedRoute>
                <CreditLogPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/diagnosis"
            element={
              <ProtectedRoute>
                <XhsDiagnosisPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/library"
            element={
              <ProtectedRoute>
                <LibraryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/persona"
            element={
              <ProtectedRoute>
                <PersonaPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/content"
            element={
              <ProtectedRoute>
                <ContentPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/project"
            element={
              <ProtectedRoute>
                <ProjectPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/assets"
            element={
              <ProtectedRoute>
                <AssetsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/templates"
            element={
              <ProtectedRoute>
                <TemplatesPage />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}